import { BaseApiService } from './BaseApiService';
import { formatDate, formatDateTime } from '../utils/date';
import {
  formatTimeForDisplay,
  formatTimeForApi,
  formatTimeFromApi,
  calculateEffectiveHours,
  validateWorkingHours,
} from '../utils/TimeUtils';

export interface WeeklySchedule {
  id: number;
  day_of_week: number;
  // NOTE: Backend does not currently return a day label; derive on the client using day_of_week.
  day_name?: string;
  worker_hours: number;
  workers: number;
  is_active: boolean;
  // Enhanced working hours fields
  start_time?: string; // HH:MM:SS format
  end_time?: string; // HH:MM:SS format
  break_start_time?: string; // HH:MM:SS format
  break_end_time?: string; // HH:MM:SS format
  created_at: string;
  updated_at: string;
}

export interface ScheduleOverride {
  id: number;
  override_date: string;
  worker_hours: number;
  workers: number;
  reason: string;
  is_active: boolean;
  // Enhanced working hours fields
  start_time?: string; // HH:MM:SS format
  end_time?: string; // HH:MM:SS format
  break_start_time?: string; // HH:MM:SS format
  break_end_time?: string; // HH:MM:SS format
  created_at: string;
  updated_at: string;
}

export interface CreateScheduleOverrideData {
  override_date: string;
  worker_hours: number;
  workers: number;
  reason: string;
  // Enhanced working hours fields
  start_time?: string; // HH:MM format
  end_time?: string; // HH:MM format
  break_start_time?: string; // HH:MM format
  break_end_time?: string; // HH:MM format
}

// Enhanced working hours interfaces
export interface EnhancedWeeklyScheduleUpdate {
  worker_hours: number;
  workers: number;
  is_active: boolean;
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
  break_start_time: string; // HH:MM format
  break_end_time: string; // HH:MM format
}

export interface EnhancedScheduleOverrideData {
  override_date: string;
  end_date?: string; // Optional end date for date ranges
  worker_hours: number;
  workers: number;
  reason: string;
  is_active?: boolean;
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
  break_start_time: string; // HH:MM format
  break_end_time: string; // HH:MM format
}

export interface WorkingHoursConfig {
  startTime: string;
  endTime: string;
  breakStartTime: string;
  breakEndTime: string;
  workers: number;
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

export interface BreakOverlapCheck {
  startTime: string;
  endTime: string;
  overlapsBreak: boolean;
  breakPeriod?: {
    startTime: string;
    endTime: string;
  };
}

export interface TimeSplitResult {
  beforeBreak?: TimeSlot;
  afterBreak?: TimeSlot;
  breakPeriod: TimeSlot;
}

export interface AvailableHours {
  date: string;
  available_hours: number;
  workers: number;
}

class SchedulingService extends BaseApiService {
  // Weekly Schedule Management
  async getWeeklySchedule(): Promise<WeeklySchedule[]> {
    const response = await this.get<{ success: boolean; data: WeeklySchedule[] }>(
      '/scheduling/weekly'
    );
    return response.data || [];
  }

  async updateWeeklySchedule(
    dayOfWeek: number,
    workerHours: number,
    workers: number = 12,
    isActive: boolean = true
  ): Promise<WeeklySchedule> {
    const response = await this.put<{ success: boolean; data: WeeklySchedule }>(
      `/scheduling/weekly/${dayOfWeek}`,
      {
        worker_hours: workerHours,
        workers: workers,
        is_active: isActive,
      }
    );
    if (!response.data) {
      throw new Error('Failed to update weekly schedule');
    }
    return response.data;
  }

  // Enhanced weekly schedule update with detailed time controls
  async updateEnhancedWeeklySchedule(
    dayOfWeek: number,
    data: EnhancedWeeklyScheduleUpdate
  ): Promise<WeeklySchedule> {
    const response = await this.put<{ success: boolean; data: WeeklySchedule }>(
      `/scheduling/weekly/${dayOfWeek}/enhanced`,
      data
    );
    if (!response.data) {
      throw new Error('Failed to update enhanced weekly schedule');
    }
    return response.data;
  }

  // Schedule Overrides Management
  async getScheduleOverrides(startDate?: string, endDate?: string): Promise<ScheduleOverride[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.get<{ success: boolean; data: ScheduleOverride[] }>(
      '/scheduling/overrides',
      params
    );
    return response.data || [];
  }

  async createScheduleOverride(data: CreateScheduleOverrideData): Promise<ScheduleOverride> {
    const response = await this.post<{ success: boolean; data: ScheduleOverride }>(
      '/scheduling/overrides',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create schedule override');
    }
    return response.data;
  }

  // Enhanced schedule override with detailed time controls
  async createEnhancedScheduleOverride(
    data: EnhancedScheduleOverrideData
  ): Promise<ScheduleOverride> {
    const response = await this.post<{ success: boolean; data: ScheduleOverride }>(
      '/scheduling/overrides/enhanced',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create enhanced schedule override');
    }
    return response.data;
  }

  async deleteScheduleOverride(date: string): Promise<void> {
    // Ensure date is in YYYY-MM-DD format for the API
    const formattedDate = date.includes('T') ? date.split('T')[0] : date;
    await this.delete<{ success: boolean }>(`/scheduling/overrides/${formattedDate}`);
  }

  // Available Hours
  async getAvailableHours(date: string): Promise<AvailableHours> {
    const response = await this.get<{ success: boolean; data: AvailableHours }>(
      `/scheduling/available-hours/${date}`
    );
    if (!response.data) {
      throw new Error('Failed to get available hours');
    }
    return response.data;
  }

  // Batch Available Hours - Get available hours for multiple dates with working hours info
  async getBatchAvailableHours(dates: string[]): Promise<
    Array<{
      date: string;
      available_hours: number;
      workers: number;
      start_time?: string;
      end_time?: string;
      break_start_time?: string;
      break_end_time?: string;
      is_active: boolean;
    }>
  > {
    const response = await this.post<{
      success: boolean;
      data: Array<{
        date: string;
        available_hours: number;
        workers: number;
        start_time?: string;
        end_time?: string;
        break_start_time?: string;
        break_end_time?: string;
        is_active: boolean;
      }>;
    }>('/scheduling/available-hours/batch', { dates });
    return response.data || [];
  }

  // Enhanced Working Hours API Methods

  // Get complete working hours configuration for a specific date
  async getWorkingHoursConfig(date: string): Promise<WorkingHoursConfig> {
    const response = await this.get<{ success: boolean; data: WorkingHoursConfig }>(
      `/scheduling/working-hours/${date}`
    );
    if (!response.data) {
      throw new Error('Failed to get working hours configuration');
    }
    return response.data;
  }

  // Get available time slots for a date (excluding break periods)
  async getAvailableTimeSlots(date: string): Promise<TimeSlot[]> {
    const response = await this.get<{ success: boolean; data: TimeSlot[] }>(
      `/scheduling/time-slots/${date}`
    );
    return response.data || [];
  }

  // Check if a time period overlaps with break periods
  async checkBreakOverlap(
    date: string,
    startTime: string,
    endTime: string
  ): Promise<BreakOverlapCheck> {
    const response = await this.post<{ success: boolean; data: BreakOverlapCheck }>(
      '/scheduling/check-break-overlap',
      {
        date,
        startTime,
        endTime,
      }
    );
    if (!response.data) {
      throw new Error('Failed to check break overlap');
    }
    return response.data;
  }

  // Split a time period around break times
  async splitTimeAroundBreak(
    date: string,
    startTime: string,
    endTime: string
  ): Promise<TimeSplitResult> {
    const response = await this.post<{ success: boolean; data: TimeSplitResult }>(
      '/scheduling/split-around-break',
      {
        date,
        startTime,
        endTime,
      }
    );
    if (!response.data) {
      throw new Error('Failed to split time around break');
    }
    return response.data;
  }

  // Get enhanced available hours with time slot information
  async getEnhancedBatchAvailableHours(
    dates: string[]
  ): Promise<Array<AvailableHours & { timeSlots: TimeSlot[] }>> {
    const response = await this.post<{
      success: boolean;
      data: Array<AvailableHours & { timeSlots: TimeSlot[] }>;
    }>('/scheduling/enhanced-available-hours/batch', { dates });
    return response.data || [];
  }

  // Utility functions
  getDayName(dayOfWeek: number): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || 'Unknown';
  }

  getDayShortName(dayOfWeek: number): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[dayOfWeek] || 'Unknown';
  }

  // Use centralized date utilities instead of duplicate methods
  formatDate(dateString: string): string {
    return formatDate(dateString);
  }

  formatDateTime(dateString: string): string {
    return formatDateTime(dateString);
  }

  // Time formatting utilities
  formatTimeForDisplay(timeString: string): string {
    return formatTimeForDisplay(timeString);
  }

  // Convert HH:MM format to HH:MM:SS for API
  formatTimeForApi(timeString: string): string {
    return formatTimeForApi(timeString);
  }

  // Convert HH:MM:SS format to HH:MM for display
  formatTimeFromApi(timeString: string): string {
    return formatTimeFromApi(timeString);
  }

  // Calculate effective working hours excluding break time
  calculateEffectiveHours(
    startTime: string,
    endTime: string,
    breakStartTime: string,
    breakEndTime: string
  ): number {
    return calculateEffectiveHours(startTime, endTime, breakStartTime, breakEndTime);
  }

  // Validate working hours configuration
  validateWorkingHours(
    startTime: string,
    endTime: string,
    breakStartTime: string,
    breakEndTime: string
  ): {
    isValid: boolean;
    errors: string[];
  } {
    return validateWorkingHours(startTime, endTime, breakStartTime, breakEndTime);
  }
}

export const schedulingService = new SchedulingService();
export default schedulingService;
